// Professional Canva-style full-page layout templates.
// All blocks are pre-positioned on an A4 canvas (794 × 1123 px).
// Uses only existing block types consistent with the editor block system.
// Accent color: #3790C5 — substituted at insert time with the page's detected accent.

export type LayoutTemplate = {
  id: string;
  name: string;
  description: string;
  category: 'document' | 'data' | 'design';
  thumbnailColor: string;
  blocks: LayoutBlock[];
};

export type LayoutBlock = {
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: Record<string, unknown>;
  style: Record<string, unknown>;
  zIndex: number;
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. Cover Page
// ─────────────────────────────────────────────────────────────────────────────
const coverPageBlocks: LayoutBlock[] = [
  // Full-width accent background
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 1123 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 0,
  },
  // Right accent panel (semi-transparent white)
  {
    type: 'shape',
    position: { x: 560, y: 0 },
    size: { width: 234, height: 1123 },
    content: { shapeType: 'rectangle', fillColor: '#ffffff', strokeWidth: 0 },
    style: { backgroundColor: '#ffffff', opacity: 0.08 },
    zIndex: 1,
  },
  // Bottom accent bar
  {
    type: 'shape',
    position: { x: 0, y: 1020 },
    size: { width: 560, height: 6 },
    content: { shapeType: 'rectangle', fillColor: '#3bc8a8', strokeWidth: 0 },
    style: { backgroundColor: '#3bc8a8' },
    zIndex: 2,
  },
  // Company name
  {
    type: 'heading1',
    position: { x: 48, y: 80 },
    size: { width: 500, height: 60 },
    content: { text: '{{company_name}}' },
    style: { color: '#ffffff', fontSize: '32px', fontWeight: 'bold', letterSpacing: '4px' },
    zIndex: 3,
  },
  // Document title
  {
    type: 'heading1',
    position: { x: 48, y: 340 },
    size: { width: 480, height: 80 },
    content: { text: '{{document_title}}' },
    style: { color: '#ffffff', fontSize: '36px', fontWeight: '800', lineHeight: '1.2' },
    zIndex: 4,
  },
  // Tagline
  {
    type: 'paragraph',
    position: { x: 48, y: 440 },
    size: { width: 480, height: 50 },
    content: { text: '{{company_tagline}}' },
    style: { color: '#d4e9f7', fontSize: '13px', fontWeight: '600' },
    zIndex: 5,
  },
  // Divider
  {
    type: 'divider',
    position: { x: 48, y: 505 },
    size: { width: 60, height: 3 },
    content: { orientation: 'horizontal', thickness: 3, color: '#3bc8a8' },
    style: { backgroundColor: '#3bc8a8' },
    zIndex: 6,
  },
  // Prepared for label
  {
    type: 'text',
    position: { x: 48, y: 540 },
    size: { width: 160, height: 20 },
    content: { text: 'Prepared for:' },
    style: { color: '#a0cce0', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' },
    zIndex: 7,
  },
  // Client name variable
  {
    type: 'text',
    position: { x: 48, y: 562 },
    size: { width: 400, height: 30 },
    content: { text: '{{client_name}}' },
    style: { color: '#ffffff', fontSize: '18px', fontWeight: '700' },
    zIndex: 8,
  },
  // Reference / ID
  {
    type: 'text',
    position: { x: 48, y: 600 },
    size: { width: 400, height: 22 },
    content: { text: 'Ref: {{document_ref}}' },
    style: { color: '#d4e9f7', fontSize: '12px' },
    zIndex: 9,
  },
  // Date
  {
    type: 'text',
    position: { x: 48, y: 625 },
    size: { width: 400, height: 22 },
    content: { text: '{{report_date}}' },
    style: { color: '#d4e9f7', fontSize: '12px' },
    zIndex: 10,
  },
  // Prepared by
  {
    type: 'text',
    position: { x: 48, y: 650 },
    size: { width: 400, height: 22 },
    content: { text: 'Prepared by: {{prepared_by}}' },
    style: { color: '#d4e9f7', fontSize: '12px' },
    zIndex: 11,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 2. Executive Summary
// ─────────────────────────────────────────────────────────────────────────────
const executiveSummaryBlocks: LayoutBlock[] = [
  // Header bar
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 0,
  },
  // Header title
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Executive Summary' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  // Client name top-right
  {
    type: 'text',
    position: { x: 650, y: 28 },
    size: { width: 120, height: 24 },
    content: { text: '{{client_name}}' },
    style: { color: '#d4e9f7', fontSize: '11px', textAlign: 'right' },
    zIndex: 2,
  },
  // Accent left bar
  {
    type: 'shape',
    position: { x: 0, y: 80 },
    size: { width: 5, height: 1043 },
    content: { shapeType: 'rectangle', fillColor: '#3bc8a8', strokeWidth: 0 },
    style: { backgroundColor: '#3bc8a8' },
    zIndex: 3,
  },
  // Section heading
  {
    type: 'heading2',
    position: { x: 40, y: 110 },
    size: { width: 720, height: 36 },
    content: { text: 'About This Report' },
    style: { color: '#3790C5', fontSize: '18px', fontWeight: '700' },
    zIndex: 4,
  },
  // Body text
  {
    type: 'paragraph',
    position: { x: 40, y: 155 },
    size: { width: 714, height: 80 },
    content: { text: 'This report has been prepared to summarise the findings and recommendations for {{client_name}}. It provides a clear overview of the options available and highlights the recommended course of action.' },
    style: { color: '#374151', fontSize: '13px', lineHeight: '1.6' },
    zIndex: 5,
  },
  // Divider
  {
    type: 'divider',
    position: { x: 40, y: 250 },
    size: { width: 714, height: 1 },
    content: { orientation: 'horizontal', thickness: 1, color: '#e5e7eb' },
    style: { backgroundColor: '#e5e7eb' },
    zIndex: 6,
  },
  // Key figures heading
  {
    type: 'heading3',
    position: { x: 40, y: 270 },
    size: { width: 300, height: 30 },
    content: { text: 'Key Figures' },
    style: { color: '#3790C5', fontSize: '15px', fontWeight: '700' },
    zIndex: 7,
  },
  // KPI boxes (3-column)
  {
    type: 'kpi-card',
    position: { x: 40, y: 310 },
    size: { width: 218, height: 110 },
    content: { title: 'Total Usage', value: '{{metric_1}}', trend: 'neutral', trendValue: '' },
    style: { backgroundColor: '#e0f0ff', borderRadius: '8px' },
    zIndex: 8,
  },
  {
    type: 'kpi-card',
    position: { x: 278, y: 310 },
    size: { width: 218, height: 110 },
    content: { title: 'Current Rate', value: '{{metric_2}}', trend: 'down', trendValue: '' },
    style: { backgroundColor: '#f0fdf4', borderRadius: '8px' },
    zIndex: 9,
  },
  {
    type: 'kpi-card',
    position: { x: 516, y: 310 },
    size: { width: 218, height: 110 },
    content: { title: 'Estimated Saving', value: '{{metric_3}}', trend: 'up', trendValue: '' },
    style: { backgroundColor: '#fefce8', borderRadius: '8px' },
    zIndex: 10,
  },
  // Recommendation heading
  {
    type: 'heading3',
    position: { x: 40, y: 450 },
    size: { width: 400, height: 30 },
    content: { text: 'Our Recommendation' },
    style: { color: '#3790C5', fontSize: '15px', fontWeight: '700' },
    zIndex: 11,
  },
  {
    type: 'callout',
    position: { x: 40, y: 490 },
    size: { width: 714, height: 80 },
    content: { text: '{{recommendation_summary}}', calloutType: 'info', title: 'Best Match' },
    style: { backgroundColor: '#eff6ff', borderRadius: '8px' },
    zIndex: 12,
  },
  // Prepared by footer
  {
    type: 'text',
    position: { x: 40, y: 600 },
    size: { width: 714, height: 20 },
    content: { text: 'Prepared by {{prepared_by}} on {{report_date}}' },
    style: { color: '#9ca3af', fontSize: '11px' },
    zIndex: 13,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 3. Comparison Table
// ─────────────────────────────────────────────────────────────────────────────
const comparisonTableBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#2c73a0', strokeWidth: 0 },
    style: { backgroundColor: '#2c73a0' },
    zIndex: 0,
  },
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Option Comparison' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  {
    type: 'text',
    position: { x: 40, y: 100 },
    size: { width: 714, height: 26 },
    content: { text: 'The following table compares the available options for {{client_name}}.' },
    style: { color: '#6b7280', fontSize: '12px' },
    zIndex: 2,
  },
  {
    type: 'table',
    position: { x: 40, y: 140 },
    size: { width: 714, height: 420 },
    content: {
      rows: [
        { cells: ['Rank', 'Option', 'Term', 'Rate', 'Daily Charge', 'Annual Cost', 'Saving'], isHeader: true },
        { cells: ['1st', '{{option_1_name}}', '{{option_1_term}}', '{{option_1_rate}}', '{{option_1_daily}}', '{{option_1_annual}}', '{{option_1_saving}}'], isHeader: false },
        { cells: ['2nd', '{{option_2_name}}', '{{option_2_term}}', '{{option_2_rate}}', '{{option_2_daily}}', '{{option_2_annual}}', '{{option_2_saving}}'], isHeader: false },
        { cells: ['3rd', '{{option_3_name}}', '{{option_3_term}}', '{{option_3_rate}}', '{{option_3_daily}}', '{{option_3_annual}}', '{{option_3_saving}}'], isHeader: false },
        { cells: ['4th', '{{option_4_name}}', '{{option_4_term}}', '{{option_4_rate}}', '{{option_4_daily}}', '{{option_4_annual}}', '{{option_4_saving}}'], isHeader: false },
      ],
      headerStyle: { backgroundColor: '#3790C5', textColor: '#ffffff', fontWeight: 'bold', textAlign: 'center', fontSize: 12 },
      tableStyle: { borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', backgroundColor: '#ffffff' },
      columns: [],
    },
    style: { borderRadius: '8px', overflow: 'hidden' },
    zIndex: 3,
  },
  {
    type: 'callout',
    position: { x: 40, y: 580 },
    size: { width: 714, height: 60 },
    content: { text: 'All rates are indicative and subject to change. Confirm details with the provider before signing.', calloutType: 'warning', title: 'Disclaimer' },
    style: { backgroundColor: '#fffbeb', borderRadius: '6px' },
    zIndex: 4,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 4. Account Details (two-column)
// ─────────────────────────────────────────────────────────────────────────────
const accountDetailsBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 0,
  },
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Account Details' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  {
    type: 'heading3',
    position: { x: 40, y: 105 },
    size: { width: 400, height: 28 },
    content: { text: 'Client Information' },
    style: { color: '#3790C5', fontSize: '15px', fontWeight: '700' },
    zIndex: 2,
  },
  {
    type: 'table',
    position: { x: 40, y: 140 },
    size: { width: 340, height: 200 },
    content: {
      rows: [
        { cells: ['Field', 'Value'], isHeader: true },
        { cells: ['Client Name', '{{client_name}}'], isHeader: false },
        { cells: ['Reference', '{{document_ref}}'], isHeader: false },
        { cells: ['Address', '{{client_address}}'], isHeader: false },
        { cells: ['Contact', '{{contact_name}}'], isHeader: false },
        { cells: ['Email', '{{contact_email}}'], isHeader: false },
      ],
      headerStyle: { backgroundColor: '#e0f0ff', textColor: '#3790C5', fontWeight: 'bold', fontSize: 12 },
      tableStyle: { borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', backgroundColor: '#ffffff' },
      columns: [],
    },
    style: { borderRadius: '6px' },
    zIndex: 3,
  },
  {
    type: 'heading3',
    position: { x: 420, y: 105 },
    size: { width: 334, height: 28 },
    content: { text: 'Usage Profile' },
    style: { color: '#3790C5', fontSize: '15px', fontWeight: '700' },
    zIndex: 4,
  },
  {
    type: 'table',
    position: { x: 420, y: 140 },
    size: { width: 334, height: 200 },
    content: {
      rows: [
        { cells: ['Period', 'Value'], isHeader: true },
        { cells: ['Annual Total', '{{annual_total}}'], isHeader: false },
        { cells: ['Peak', '{{peak_value}}'], isHeader: false },
        { cells: ['Off-Peak', '{{offpeak_value}}'], isHeader: false },
        { cells: ['Controlled', '{{controlled_value}}'], isHeader: false },
      ],
      headerStyle: { backgroundColor: '#e0f0ff', textColor: '#3790C5', fontWeight: 'bold', fontSize: 12 },
      tableStyle: { borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', backgroundColor: '#ffffff' },
      columns: [],
    },
    style: { borderRadius: '6px' },
    zIndex: 5,
  },
  {
    type: 'heading3',
    position: { x: 40, y: 365 },
    size: { width: 400, height: 28 },
    content: { text: 'Current Contract' },
    style: { color: '#3790C5', fontSize: '15px', fontWeight: '700' },
    zIndex: 6,
  },
  {
    type: 'table',
    position: { x: 40, y: 400 },
    size: { width: 714, height: 160 },
    content: {
      rows: [
        { cells: ['Provider', 'Start Date', 'End Date', 'Rate', 'Contract Type'], isHeader: true },
        { cells: ['{{current_provider}}', '{{contract_start}}', '{{contract_end}}', '{{current_rate}}', '{{contract_type}}'], isHeader: false },
      ],
      headerStyle: { backgroundColor: '#e0f0ff', textColor: '#3790C5', fontWeight: 'bold', fontSize: 12 },
      tableStyle: { borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', backgroundColor: '#ffffff' },
      columns: [],
    },
    style: { borderRadius: '6px' },
    zIndex: 7,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 5. Cost Overview (dashboard)
// ─────────────────────────────────────────────────────────────────────────────
const costOverviewBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 0,
  },
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Cost Overview' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  // 3 KPI cards row
  {
    type: 'kpi-card',
    position: { x: 40, y: 110 },
    size: { width: 218, height: 110 },
    content: { title: 'Total Annual Cost', value: '{{total_annual_cost}}', trend: 'neutral', trendValue: '' },
    style: { backgroundColor: '#e0f0ff', borderRadius: '10px' },
    zIndex: 2,
  },
  {
    type: 'kpi-card',
    position: { x: 278, y: 110 },
    size: { width: 218, height: 110 },
    content: { title: 'Unit Rate', value: '{{cost_per_unit}}', trend: 'down', trendValue: '-{{saving_pct}}%' },
    style: { backgroundColor: '#f0fdf4', borderRadius: '10px' },
    zIndex: 3,
  },
  {
    type: 'kpi-card',
    position: { x: 516, y: 110 },
    size: { width: 218, height: 110 },
    content: { title: 'Monthly Average', value: '{{monthly_cost}}', trend: 'neutral', trendValue: '' },
    style: { backgroundColor: '#fefce8', borderRadius: '10px' },
    zIndex: 4,
  },
  // Bar chart - usage by period
  {
    type: 'bar-chart',
    position: { x: 40, y: 245 },
    size: { width: 440, height: 260 },
    content: {
      title: 'Usage by Category',
      data: {
        labels: ['Q1', 'Q2', 'Q3', 'Q4'],
        datasets: [{
          label: 'Usage',
          data: [12500, 18400, 8200, 3100],
          backgroundColor: ['#3790C5', '#3bc8a8', '#f59e0b', '#8b5cf6'],
        }],
      },
    },
    style: { backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    zIndex: 5,
  },
  // Cost breakdown table
  {
    type: 'table',
    position: { x: 500, y: 245 },
    size: { width: 254, height: 260 },
    content: {
      rows: [
        { cells: ['Component', 'Cost'], isHeader: true },
        { cells: ['Usage Charges', '{{usage_cost}}'], isHeader: false },
        { cells: ['Supply Charges', '{{supply_cost}}'], isHeader: false },
        { cells: ['Network Fees', '{{network_cost}}'], isHeader: false },
        { cells: ['Tax (10%)', '{{tax_amount}}'], isHeader: false },
        { cells: ['Total', '{{total_annual_cost}}'], isHeader: false },
      ],
      headerStyle: { backgroundColor: '#3790C5', textColor: '#ffffff', fontWeight: 'bold', fontSize: 12 },
      tableStyle: { borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', backgroundColor: '#ffffff' },
      columns: [],
    },
    style: { borderRadius: '6px' },
    zIndex: 6,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 6. Terms & Conditions
// ─────────────────────────────────────────────────────────────────────────────
const termsBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#374151', strokeWidth: 0 },
    style: { backgroundColor: '#374151' },
    zIndex: 0,
  },
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Terms & Conditions' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  {
    type: 'heading3',
    position: { x: 40, y: 110 },
    size: { width: 714, height: 28 },
    content: { text: '1. Disclaimer' },
    style: { color: '#374151', fontSize: '14px', fontWeight: '700' },
    zIndex: 2,
  },
  {
    type: 'paragraph',
    position: { x: 40, y: 145 },
    size: { width: 714, height: 80 },
    content: { text: 'The information contained in this report has been prepared based on data provided by the client and publicly available market information. All rates and costs are indicative only and subject to change. {{company_name}} accepts no liability for decisions made based on this report.' },
    style: { color: '#4b5563', fontSize: '12px', lineHeight: '1.7' },
    zIndex: 3,
  },
  {
    type: 'heading3',
    position: { x: 40, y: 240 },
    size: { width: 714, height: 28 },
    content: { text: '2. Privacy & Confidentiality' },
    style: { color: '#374151', fontSize: '14px', fontWeight: '700' },
    zIndex: 4,
  },
  {
    type: 'paragraph',
    position: { x: 40, y: 275 },
    size: { width: 714, height: 80 },
    content: { text: 'This report is prepared exclusively for {{client_name}} and is confidential. It must not be disclosed to third parties without the prior written consent of {{company_name}}. All personal and business information is handled in accordance with applicable privacy legislation.' },
    style: { color: '#4b5563', fontSize: '12px', lineHeight: '1.7' },
    zIndex: 5,
  },
  {
    type: 'heading3',
    position: { x: 40, y: 370 },
    size: { width: 714, height: 28 },
    content: { text: '3. Market Information' },
    style: { color: '#374151', fontSize: '14px', fontWeight: '700' },
    zIndex: 6,
  },
  {
    type: 'paragraph',
    position: { x: 40, y: 405 },
    size: { width: 714, height: 80 },
    content: { text: 'Market prices are subject to change at any time. The information presented in this report was valid at the time of preparation. {{company_name}} recommends confirming all details directly with the relevant provider prior to signing any agreement.' },
    style: { color: '#4b5563', fontSize: '12px', lineHeight: '1.7' },
    zIndex: 7,
  },
  // Signature section
  {
    type: 'divider',
    position: { x: 40, y: 560 },
    size: { width: 714, height: 1 },
    content: { orientation: 'horizontal', thickness: 1, color: '#e5e7eb' },
    style: { backgroundColor: '#e5e7eb' },
    zIndex: 8,
  },
  {
    type: 'text',
    position: { x: 40, y: 580 },
    size: { width: 300, height: 20 },
    content: { text: 'Client Signature' },
    style: { color: '#6b7280', fontSize: '11px', fontWeight: '600' },
    zIndex: 9,
  },
  {
    type: 'signature',
    position: { x: 40, y: 605 },
    size: { width: 300, height: 70 },
    content: { placeholder: 'Sign here' },
    style: { border: '1px dashed #d1d5db', borderRadius: '4px' },
    zIndex: 10,
  },
  {
    type: 'text',
    position: { x: 40, y: 685 },
    size: { width: 300, height: 20 },
    content: { text: 'Date: _______________________' },
    style: { color: '#6b7280', fontSize: '11px' },
    zIndex: 11,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 7. Contact & Sign-off
// ─────────────────────────────────────────────────────────────────────────────
const contactPageBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 0,
  },
  {
    type: 'heading2',
    position: { x: 40, y: 22 },
    size: { width: 500, height: 40 },
    content: { text: 'Contact Us' },
    style: { color: '#ffffff', fontSize: '22px', fontWeight: '700' },
    zIndex: 1,
  },
  // Footer branding block
  {
    type: 'shape',
    position: { x: 0, y: 820 },
    size: { width: 794, height: 303 },
    content: { shapeType: 'rectangle', fillColor: '#2c73a0', strokeWidth: 0 },
    style: { backgroundColor: '#2c73a0' },
    zIndex: 2,
  },
  // Thank you text
  {
    type: 'heading1',
    position: { x: 40, y: 850 },
    size: { width: 714, height: 60 },
    content: { text: 'Thank you for choosing\n{{company_name}}' },
    style: { color: '#ffffff', fontSize: '28px', fontWeight: '800', lineHeight: '1.2' },
    zIndex: 3,
  },
  {
    type: 'paragraph',
    position: { x: 40, y: 940 },
    size: { width: 500, height: 40 },
    content: { text: '{{company_tagline}}' },
    style: { color: '#a0cce0', fontSize: '13px' },
    zIndex: 4,
  },
  // Contact cards
  {
    type: 'callout',
    position: { x: 40, y: 120 },
    size: { width: 340, height: 100 },
    content: { title: 'Your Consultant', text: '{{prepared_by}}\n{{consultant_phone}}\n{{consultant_email}}', calloutType: 'info' },
    style: { backgroundColor: '#eff6ff', borderRadius: '8px' },
    zIndex: 5,
  },
  {
    type: 'callout',
    position: { x: 414, y: 120 },
    size: { width: 340, height: 100 },
    content: { title: '{{company_name}} Office', text: 'Phone: {{company_phone}}\nEmail: {{company_email}}\nWeb: {{company_website}}', calloutType: 'info' },
    style: { backgroundColor: '#eff6ff', borderRadius: '8px' },
    zIndex: 6,
  },
  {
    type: 'paragraph',
    position: { x: 40, y: 245 },
    size: { width: 714, height: 40 },
    content: { text: 'Prepared on {{report_date}} for {{client_name}}' },
    style: { color: '#6b7280', fontSize: '11px' },
    zIndex: 7,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 8. Blank Professional Grid
// ─────────────────────────────────────────────────────────────────────────────
const blankGridBlocks: LayoutBlock[] = [
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 794, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#f9fafb', strokeWidth: 0 },
    style: { backgroundColor: '#f9fafb' },
    zIndex: 0,
  },
  {
    type: 'shape',
    position: { x: 0, y: 0 },
    size: { width: 4, height: 80 },
    content: { shapeType: 'rectangle', fillColor: '#3790C5', strokeWidth: 0 },
    style: { backgroundColor: '#3790C5' },
    zIndex: 1,
  },
  {
    type: 'heading2',
    position: { x: 24, y: 22 },
    size: { width: 400, height: 40 },
    content: { text: '{{page_title}}' },
    style: { color: '#111827', fontSize: '22px', fontWeight: '700' },
    zIndex: 2,
  },
  {
    type: 'text',
    position: { x: 24, y: 100 },
    size: { width: 746, height: 24 },
    content: { text: '{{page_subtitle}}' },
    style: { color: '#6b7280', fontSize: '13px' },
    zIndex: 3,
  },
  {
    type: 'divider',
    position: { x: 24, y: 134 },
    size: { width: 746, height: 1 },
    content: { orientation: 'horizontal', thickness: 1, color: '#e5e7eb' },
    style: { backgroundColor: '#e5e7eb' },
    zIndex: 4,
  },
  // Two equal columns
  {
    type: 'shape',
    position: { x: 24, y: 155 },
    size: { width: 360, height: 300 },
    content: { shapeType: 'rectangle', fillColor: '#f3f4f6', strokeWidth: 1 },
    style: { backgroundColor: '#f3f4f6', border: '1px dashed #d1d5db', borderRadius: '6px' },
    zIndex: 5,
  },
  {
    type: 'shape',
    position: { x: 410, y: 155 },
    size: { width: 360, height: 300 },
    content: { shapeType: 'rectangle', fillColor: '#f3f4f6', strokeWidth: 1 },
    style: { backgroundColor: '#f3f4f6', border: '1px dashed #d1d5db', borderRadius: '6px' },
    zIndex: 6,
  },
  {
    type: 'text',
    position: { x: 24, y: 475 },
    size: { width: 746, height: 24 },
    content: { text: 'Drag and drop blocks here to build your layout.' },
    style: { color: '#9ca3af', fontSize: '12px', textAlign: 'center' },
    zIndex: 7,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────
export const PAGE_LAYOUT_TEMPLATES: LayoutTemplate[] = [
  {
    id: 'cover-page',
    name: 'Cover Page',
    description: 'Full-bleed cover with company branding and document title',
    category: 'design',
    thumbnailColor: 'linear-gradient(135deg, #3790C5, #3bc8a8)',
    blocks: coverPageBlocks,
  },
  {
    id: 'executive-summary',
    name: 'Executive Summary',
    description: 'Summary page with KPI cards and intro text',
    category: 'document',
    thumbnailColor: 'linear-gradient(135deg, #3790C5, #60a5fa)',
    blocks: executiveSummaryBlocks,
  },
  {
    id: 'comparison-table',
    name: 'Comparison Table',
    description: 'Side-by-side option comparison with ranked table',
    category: 'data',
    thumbnailColor: 'linear-gradient(135deg, #2c73a0, #3790C5)',
    blocks: comparisonTableBlocks,
  },
  {
    id: 'account-details',
    name: 'Account Details',
    description: 'Two-column account info, usage profile, and current contract',
    category: 'data',
    thumbnailColor: 'linear-gradient(135deg, #3790C5, #93c5fd)',
    blocks: accountDetailsBlocks,
  },
  {
    id: 'cost-overview',
    name: 'Cost Overview',
    description: 'Dashboard with KPI cards, bar chart, and cost breakdown table',
    category: 'data',
    thumbnailColor: 'linear-gradient(135deg, #3790C5, #34d399)',
    blocks: costOverviewBlocks,
  },
  {
    id: 'terms-conditions',
    name: 'Terms & Conditions',
    description: 'Legal disclaimer, confidentiality, and client signature',
    category: 'document',
    thumbnailColor: 'linear-gradient(135deg, #374151, #6b7280)',
    blocks: termsBlocks,
  },
  {
    id: 'contact-page',
    name: 'Contact & Sign-off',
    description: 'Closing page with consultant and company contact details',
    category: 'design',
    thumbnailColor: 'linear-gradient(135deg, #2c73a0, #3bc8a8)',
    blocks: contactPageBlocks,
  },
  {
    id: 'blank-grid',
    name: 'Blank Grid',
    description: 'Clean two-column grid ready for your content',
    category: 'design',
    thumbnailColor: 'linear-gradient(135deg, #e0f0ff, #bfdbfe)',
    blocks: blankGridBlocks,
  },
];
